import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import Header from "../../../components/Header";
import LoadingOverlay from "../../../components/LoadingOverlay";
import Sidebar from "../../../components/Sidebar";
import {
    DUE_DATE_STATUSES,
    getDueDateStatusFromDate,
} from "../../../helpers/dueDateStatus";
import {
    getDueTodayTextClass,
    isDueTodayLabel,
} from "../../../helpers/dueTodayAppearance";
import {
    ALL_REPORT_STATUSES_VALUE,
    REPORT_STATUS_OPTIONS,
} from "../../../helpers/reportStatusOptions";
import {
    getFirmAccountability,
    getServiceAccountability,
} from "../../../services/analytics";
import { getEmployees } from "../../../services/getEmployees";
import { getServices } from "../../../services/getServices";
import { getServiceDepartments } from "../../../services/service/getServiceDepartments";
import UpdateNotesInAccountabilityReportModal from "../UpdateNotesInAccountabilityReportModal";
import JobAccountabilityTable from "./components/JobAccountabilityTable";
import ServicesAccountabilityTable from "./components/ServicesAccountabilityTable";
import { usePermissions } from "../../../context/PermissionsContext";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const DEFAULT_START_DATE = new Date(2022, 0, 1);

const ASSIGNMENT_TYPES = {
    JOBS: "jobs",
    SERVICES: "services",
};

const ASSIGNMENT_OPTIONS = [
    {
        label: "Services",
        value: ASSIGNMENT_TYPES.SERVICES,
    },
    {
        label: "Steps",
        value: ASSIGNMENT_TYPES.JOBS,
    },
];

const DUE_DATE_BUCKET_ALIASES = ["pastDue", "next7days", "thereafter"];

const ALL_OPTION = {
    value: EMPTY_GUID,
    label: "All",
    is_showing: true,
};

const formatDateInputValue = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const parseDateInputValue = (value) => {
    if (!value) {
        return null;
    }

    return new Date(value);
};

const getDateValidationMessage = (startDate, endDate) => {
    if (!startDate && !endDate) {
        return "Select both Start Date and End Date before applying date filters.";
    }

    if ((startDate && !endDate) || (!startDate && endDate)) {
        return "Select both Start Date and End Date before applying date filters.";
    }

    if (new Date(startDate) > new Date(endDate)) {
        return "Start Date must be earlier than or equal to End Date.";
    }

    return "";
};

const normalizeFiltersForRequest = (filters) => ({
    startDate: new Date(filters.startDate),
    endDate: new Date(filters.endDate),
    employeeId: filters.employeeId,
    departmentId: filters.departmentId,
    serviceId: filters.serviceId,
    status: filters.status,
    includeInactive: false,
});

const isDateWithinLastSevenDays = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return false;
    }

    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return date >= sevenDaysAgo && date <= today;
};

const buildSummaryCards = (assignmentType, reportData) => {
    const totalCount = Array.isArray(reportData) ? reportData.length : 0;

    if (assignmentType === ASSIGNMENT_TYPES.SERVICES) {
        const newRows = reportData.filter((service) =>
            isDateWithinLastSevenDays(
                service.subscriptionStartDate || service.assignedDate,
            ),
        );

        return [
            {
                label: "Total Services",
                value: totalCount,
                accentClass: "text-slate-700",
                rows: reportData,
                title: "Services",
            },
            {
                label: "New Services",
                subtitle: "last 7 days",
                value: newRows.length,
                accentClass: "text-green-600",
                rows: newRows,
                title: "New Services",
            },
        ];
    }

    const newRows = reportData.filter((job) =>
        isDateWithinLastSevenDays(job.assignedDate),
    );

    return [
        {
            label: "Total Steps",
            value: totalCount,
            accentClass: "text-slate-700",
            rows: reportData,
            title: "Steps",
        },
        {
            label: "New Steps",
            subtitle: "last 7 days",
            value: newRows.length,
            accentClass: "text-green-600",
            rows: newRows,
            title: "New Steps",
        },
    ];
};

const createDueDateBuckets = (assignmentType, rows) => {
    const buckets = DUE_DATE_STATUSES.filter((status) =>
        DUE_DATE_BUCKET_ALIASES.includes(status.alias),
    ).map((status) => ({
        ...status,
        rows: [],
        dueTodayRows: [],
    }));

    rows.forEach((row) => {
        const dueDate =
            assignmentType === ASSIGNMENT_TYPES.SERVICES
                ? row.subscriptionEndDate
                : row.dueDate;
        const bucket = getDueDateStatusFromDate(dueDate);

        if (!bucket?.alias) {
            return;
        }

        const bucketMatch = buckets.find((item) => item.alias === bucket.alias);

        if (!bucketMatch) {
            return;
        }

        bucketMatch.rows.push(row);

        if (bucket.alias === "next7days" && bucket.daysDiff === 0) {
            bucketMatch.dueTodayRows.push(row);
        }
    });

    return buckets;
};

function Accountability() {
    const { can, permissionsLoading } = usePermissions();
    const canReadServices = can("client_services.read");
    const canReadSteps = can("steps.read");
    const canWriteSteps = can("steps.write");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [selectedTableView, setSelectedTableView] = useState(null);
    const [employees, setEmployees] = useState([ALL_OPTION]);
    const [departments, setDepartments] = useState([ALL_OPTION]);
    const [services, setServices] = useState([ALL_OPTION]);
    const [filters, setFilters] = useState({
        assignmentType: ASSIGNMENT_TYPES.SERVICES,
        startDate: DEFAULT_START_DATE,
        endDate: new Date(),
        employeeId: EMPTY_GUID,
        departmentId: EMPTY_GUID,
        serviceId: EMPTY_GUID,
        status: ALL_REPORT_STATUSES_VALUE,
    });
    const [appliedFilters, setAppliedFilters] = useState({
        assignmentType: ASSIGNMENT_TYPES.SERVICES,
        startDate: DEFAULT_START_DATE,
        endDate: new Date(),
        employeeId: EMPTY_GUID,
        departmentId: EMPTY_GUID,
        serviceId: EMPTY_GUID,
        status: ALL_REPORT_STATUSES_VALUE,
    });
    const [isUpdateNotesModalOpen, setIsUpdateNotesModalOpen] = useState(false);
    const [currentNotes, setCurrentNotes] = useState({
        jobId: null,
        notes: null,
    });
    const [dateValidationMessage, setDateValidationMessage] = useState("");
    const tableSectionRef = useRef(null);
    const shouldScrollToTableRef = useRef(false);
    const shouldScrollToSelectionRef = useRef(false);
    const availableAssignmentOptions = useMemo(
        () =>
            ASSIGNMENT_OPTIONS.filter((option) =>
                option.value === ASSIGNMENT_TYPES.SERVICES
                    ? canReadServices
                    : canReadSteps,
            ),
        [canReadServices, canReadSteps],
    );

    const filteredServiceOptions = useMemo(() => {
        if (filters.departmentId === EMPTY_GUID) {
            return services;
        }

        return services.filter((service) => {
            if (service.value === EMPTY_GUID) {
                return true;
            }

            return service.departmentId === filters.departmentId;
        });
    }, [filters.departmentId, services]);

    const selectedAssignmentTypeOption = useMemo(
        () =>
            availableAssignmentOptions.find(
                (option) => option.value === filters.assignmentType,
            ) ||
            availableAssignmentOptions[0] ||
            null,
        [availableAssignmentOptions, filters.assignmentType],
    );

    const selectedEmployeeOption = useMemo(
        () =>
            employees.find((option) => option.value === filters.employeeId) ||
            employees[0] ||
            ALL_OPTION,
        [employees, filters.employeeId],
    );

    const selectedDepartmentOption = useMemo(
        () =>
            departments.find((option) => option.value === filters.departmentId) ||
            departments[0] ||
            ALL_OPTION,
        [departments, filters.departmentId],
    );

    const selectedServiceOption = useMemo(
        () =>
            filteredServiceOptions.find(
                (option) => option.value === filters.serviceId,
            ) ||
            filteredServiceOptions[0] ||
            ALL_OPTION,
        [filteredServiceOptions, filters.serviceId],
    );

    const selectedStatusOption = useMemo(
        () =>
            REPORT_STATUS_OPTIONS.find(
                (option) => option.value === filters.status,
            ) || REPORT_STATUS_OPTIONS[0],
        [filters.status],
    );

    const appliedEmployeeLabel = useMemo(() => {
        if (appliedFilters.employeeId === EMPTY_GUID) {
            return "All Employees";
        }

        return (
            employees.find((option) => option.value === appliedFilters.employeeId)
                ?.label || "Selected Employee"
        );
    }, [appliedFilters.employeeId, employees]);

    const summaryCards = useMemo(
        () => buildSummaryCards(appliedFilters.assignmentType, reportData),
        [appliedFilters.assignmentType, reportData],
    );

    const dueDateBuckets = useMemo(
        () => createDueDateBuckets(appliedFilters.assignmentType, reportData),
        [appliedFilters.assignmentType, reportData],
    );

    const currentAssignmentLabel =
        appliedFilters.assignmentType === ASSIGNMENT_TYPES.JOBS
            ? "Steps"
            : "Services";
    const currentAssignmentLabelLower = currentAssignmentLabel.toLowerCase();
    const tableData = selectedTableView?.rows || reportData;

    const fetchReportData = useCallback(async () => {
        if (permissionsLoading) return;

        const canReadSelectedType =
            appliedFilters.assignmentType === ASSIGNMENT_TYPES.SERVICES
                ? canReadServices
                : canReadSteps;
        if (!canReadSelectedType) {
            setReportData([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        try {
            const normalizedFilters = normalizeFiltersForRequest(appliedFilters);

            if (appliedFilters.assignmentType === ASSIGNMENT_TYPES.SERVICES) {
                const response = await getServiceAccountability({
                    ...normalizedFilters,
                    startDate:
                        normalizedFilters.startDate.toLocaleDateString("en-US"),
                    endDate: normalizedFilters.endDate.toLocaleDateString("en-US"),
                });

                if (!response.success) {
                    throw new Error(response.message || "Error fetching services.");
                }

                setReportData(response.data?.services || []);
                return;
            }

            const response = await getFirmAccountability(normalizedFilters);

            if (!response.success) {
                throw new Error(response.message || "Error fetching steps.");
            }

            setReportData(response.data?.jobs || []);
        } catch (error) {
            setReportData([]);
            shouldScrollToTableRef.current = false;
            toast.error(error.message || "Error fetching accountability report.");
        } finally {
            setIsLoading(false);
        }
    }, [
        appliedFilters,
        canReadServices,
        canReadSteps,
        permissionsLoading,
    ]);

    useEffect(() => {
        if (permissionsLoading) return;
        const fallbackType = availableAssignmentOptions[0]?.value;
        if (!fallbackType) {
            setIsLoading(false);
            return;
        }
        if (
            !availableAssignmentOptions.some(
                (option) => option.value === filters.assignmentType,
            )
        ) {
            setFilters((current) => ({
                ...current,
                assignmentType: fallbackType,
            }));
            setAppliedFilters((current) => ({
                ...current,
                assignmentType: fallbackType,
            }));
        }
    }, [
        availableAssignmentOptions,
        filters.assignmentType,
        permissionsLoading,
    ]);

    useEffect(() => {
        let isMounted = true;

        async function loadCatalogs() {
            if (
                permissionsLoading ||
                (!canReadServices && !canReadSteps)
            )
                return;
            try {
                const [employeesResponse, departmentsResponse, servicesResponse] =
                    await Promise.all([
                        getEmployees(),
                        getServiceDepartments(),
                        getServices(),
                    ]);

                if (!isMounted) {
                    return;
                }

                if (employeesResponse.success) {
                    const employeeOptions =
                        employeesResponse.data?.employees?.map((employee) => ({
                            value: employee.employeeId,
                            label: `${employee.firstName} ${employee.lastName}`,
                            is_showing: true,
                        })) || [];

                    setEmployees([ALL_OPTION, ...employeeOptions]);
                } else {
                    toast.error("Error loading employees.");
                }

                if (
                    departmentsResponse.success &&
                    departmentsResponse.data?.success
                ) {
                    const departmentOptions =
                        departmentsResponse.data?.departments?.map((department) => ({
                            value: department.id,
                            label: department.name,
                            is_showing: true,
                        })) || [];

                    setDepartments([ALL_OPTION, ...departmentOptions]);
                } else {
                    toast.error("Error loading departments.");
                }

                if (servicesResponse.success) {
                    const serviceOptions =
                        servicesResponse.data?.map((service) => ({
                            value: service.id,
                            label: service.name,
                            is_showing: true,
                            departmentId: service.department?.id || EMPTY_GUID,
                        })) || [];

                    setServices([ALL_OPTION, ...serviceOptions]);
                } else {
                    toast.error("Error loading services.");
                }
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                toast.error("Error loading report filters.");
            }
        }

        loadCatalogs();

        return () => {
            isMounted = false;
        };
    }, [canReadServices, canReadSteps, permissionsLoading]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    useEffect(() => {
        if (isLoading || !shouldScrollToTableRef.current) {
            return;
        }

        if (tableSectionRef.current) {
            tableSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToTableRef.current = false;
    }, [isLoading, reportData, selectedTableView]);

    useEffect(() => {
        if (!selectedTableView || !shouldScrollToSelectionRef.current) {
            return;
        }

        if (tableSectionRef.current) {
            tableSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToSelectionRef.current = false;
    }, [selectedTableView]);

    useEffect(() => {
        setSelectedTableView(null);
        shouldScrollToSelectionRef.current = false;
    }, [appliedFilters]);

    const onHandleClickNotes = ({ jobId, notes }) => {
        if (!canWriteSteps) return;
        setCurrentNotes({
            jobId,
            notes,
        });
        setIsUpdateNotesModalOpen(true);
    };

    const handleFilterValueChange = (field, value) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            [field]: value,
        }));
    };

    const handleDateFilterChange = (field, value) => {
        setFilters((currentFilters) => {
            const nextFilters = {
                ...currentFilters,
                [field]: parseDateInputValue(value),
            };

            setDateValidationMessage(
                getDateValidationMessage(nextFilters.startDate, nextFilters.endDate),
            );

            return nextFilters;
        });
    };

    const handleDepartmentChange = (option) => {
        setFilters((currentFilters) => {
            const nextDepartmentId = option?.value || EMPTY_GUID;
            const nextServiceId =
                nextDepartmentId !== EMPTY_GUID &&
                currentFilters.serviceId !== EMPTY_GUID
                    ? services.find(
                          (service) =>
                              service.value === currentFilters.serviceId &&
                              service.departmentId === nextDepartmentId,
                      )
                        ? currentFilters.serviceId
                        : EMPTY_GUID
                    : currentFilters.serviceId;

            return {
                ...currentFilters,
                departmentId: nextDepartmentId,
                serviceId: nextServiceId,
            };
        });
    };

    const handleApplyFilters = () => {
        const nextDateValidationMessage = getDateValidationMessage(
            filters.startDate,
            filters.endDate,
        );

        setDateValidationMessage(nextDateValidationMessage);

        if (nextDateValidationMessage) {
            return;
        }

        shouldScrollToTableRef.current = true;
        setAppliedFilters({
            ...filters,
            startDate: filters.startDate ? new Date(filters.startDate) : null,
            endDate: filters.endDate ? new Date(filters.endDate) : null,
        });
    };

    const handleMetricSelection = (card) => {
        shouldScrollToSelectionRef.current = true;
        setSelectedTableView({
            alias: `metric-${card.label.toLowerCase().replace(/\s+/g, "-")}`,
            title: card.title,
            rows: card.rows || [],
        });
    };

    const renderMetricCard = ({ label, subtitle, value, accentClass, rows }) => (
        <div
            key={label}
            className="flex flex-col p-6 bg-teal-100 rounded-lg shadow-lg"
        >
            <h2 className="text-xl font-semibold text-gray-800">{label}</h2>
            {subtitle && (
                <p className="mt-1 text-sm font-medium text-slate-500">
                    ({subtitle})
                </p>
            )}
            <button
                type="button"
                onClick={() =>
                    Array.isArray(rows) &&
                    rows.length >= 0 &&
                    handleMetricSelection({
                        label,
                        title: label,
                        rows,
                    })
                }
                className={`mt-6 text-left text-4xl font-bold transition-transform duration-150 hover:scale-[1.02] ${accentClass}`}
            >
                {value}
            </button>
        </div>
    );

    const renderDueDateCountButton = (count, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className="mt-4 font-sans text-white transition-transform duration-150 cursor-pointer text-7xl hover:scale-105 hover:text-slate-200"
        >
            {count}
        </button>
    );

    const renderDueTodayButton = (count, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className={`mt-3 text-base font-semibold transition-transform duration-150 cursor-pointer hover:scale-105 ${getDueTodayTextClass({
                surface: "warning",
                interactive: true,
            })}`}
        >
            Due today: {count}
        </button>
    );

    const handleDueDateBucketSelection = (bucket, rows, title) => {
        shouldScrollToSelectionRef.current = true;
        setSelectedTableView({
            alias: bucket.alias,
            title,
            rows,
        });
    };

    return (
        <>
            <LoadingOverlay isLoading={permissionsLoading || isLoading} />
            <div className="flex h-screen overflow-hidden">
                <Toaster />
                {canWriteSteps && (
                    <UpdateNotesInAccountabilityReportModal
                        open={isUpdateNotesModalOpen}
                        setOpen={setIsUpdateNotesModalOpen}
                        notes={currentNotes}
                        fetchAccountability={fetchReportData}
                    />
                )}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-3xl">
                                <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                    Accountability
                                </h1>
                                {/* <p className="mt-2 text-sm text-slate-500">
                                    Review firm assignments in one place with shared
                                    filters for steps and services.
                                </p> */}
                            </div>

                            <div className="p-5 mt-6 bg-white border shadow-sm rounded-xl border-slate-200">
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-7">
                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            Assignment Type
                                        </label>
                                        <Select
                                            value={selectedAssignmentTypeOption}
                                            options={availableAssignmentOptions}
                                            classNamePrefix="select"
                                            onChange={(option) =>
                                                handleFilterValueChange(
                                                    "assignmentType",
                                                    option?.value ||
                                                        ASSIGNMENT_TYPES.SERVICES,
                                                )
                                            }
                                            isSearchable={false}
                                        />
                                    </div>

                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full border border-gray-300 rounded-md form-input"
                                            value={formatDateInputValue(
                                                filters.startDate,
                                            )}
                                            onChange={(event) =>
                                                handleDateFilterChange(
                                                    "startDate",
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            className="w-full border border-gray-300 rounded-md form-input"
                                            value={formatDateInputValue(
                                                filters.endDate,
                                            )}
                                            onChange={(event) =>
                                                handleDateFilterChange(
                                                    "endDate",
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            Employee
                                        </label>
                                        <Select
                                            value={selectedEmployeeOption}
                                            options={employees}
                                            classNamePrefix="select"
                                            onChange={(option) =>
                                                handleFilterValueChange(
                                                    "employeeId",
                                                    option?.value || EMPTY_GUID,
                                                )
                                            }
                                            isSearchable={true}
                                        />
                                    </div>

                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            Department
                                        </label>
                                        <Select
                                            value={selectedDepartmentOption}
                                            options={departments}
                                            classNamePrefix="select"
                                            onChange={handleDepartmentChange}
                                            isSearchable={true}
                                        />
                                    </div>

                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            Service
                                        </label>
                                        <Select
                                            value={selectedServiceOption}
                                            options={filteredServiceOptions}
                                            classNamePrefix="select"
                                            onChange={(option) =>
                                                handleFilterValueChange(
                                                    "serviceId",
                                                    option?.value || EMPTY_GUID,
                                                )
                                            }
                                            isSearchable={true}
                                        />
                                    </div>

                                    <div className="xl:col-span-1">
                                        <label className="block mb-2 text-sm font-semibold text-slate-700">
                                            Status
                                        </label>
                                        <Select
                                            value={selectedStatusOption}
                                            options={REPORT_STATUS_OPTIONS}
                                            classNamePrefix="select"
                                            onChange={(option) =>
                                                handleFilterValueChange(
                                                    "status",
                                                    option?.value ??
                                                        ALL_REPORT_STATUSES_VALUE,
                                                )
                                            }
                                            isSearchable={false}
                                        />
                                    </div>
                                </div>

                                {dateValidationMessage && (
                                    <p className="mt-3 text-sm font-medium text-red-600">
                                        {dateValidationMessage}
                                    </p>
                                )}

                                <div className="flex justify-end mt-5">
                                    <button
                                        type="button"
                                        onClick={handleApplyFilters}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition bg-teal-500 rounded-lg shadow-sm hover:bg-teal-600"
                                    >
                                        Apply filters
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 mt-6 md:grid-cols-2">
                                {summaryCards.map(renderMetricCard)}
                            </div>

                            <section className="mt-10">
                                <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                    <div className="mb-4 sm:mb-0">
                                        <h2 className="text-2xl font-bold text-slate-800">
                                            Due Date Summary
                                        </h2>
                                        <p className="mt-2 text-sm text-slate-500">
                                            Click a bucket to filter the current
                                            table in place.
                                        </p>
                                    </div>

                                    {/* <div className="px-4 py-3 text-sm border rounded-lg border-slate-200 bg-slate-50 text-slate-600">
                                        Showing{" "}
                                        <span className="font-semibold text-slate-800">
                                            {currentAssignmentLabel}
                                        </span>{" "}
                                        with the active global filters.
                                    </div> */}
                                </div>

                                <div className="flex flex-row mb-12 overflow-x-auto">
                                    {dueDateBuckets.map((bucket) => {
                                        const footerLabel =
                                            currentAssignmentLabel.toUpperCase();
                                        const isSelected =
                                            selectedTableView?.alias ===
                                            bucket.alias;

                                        return (
                                            <div
                                                key={bucket.alias}
                                                className={`relative mx-6 flex w-full min-w-[250px] flex-col rounded-xl p-7 text-white shadow-md shadow-gray-900/20 ${bucket.bgClass} ${
                                                    isSelected
                                                        ? "ring-4 ring-slate-200"
                                                        : ""
                                                }`}
                                            >
                                                <div className="relative flex flex-col items-center overflow-hidden text-center text-gray-700">
                                                    <p className="block text-sm font-normal leading-normal text-white uppercase">
                                                        {bucket.name}
                                                    </p>
                                                    <div className="flex justify-center gap-1">
                                                        {renderDueDateCountButton(
                                                            bucket.rows.length,
                                                            () =>
                                                                handleDueDateBucketSelection(
                                                                    bucket,
                                                                    bucket.rows,
                                                                    `${bucket.name} ${currentAssignmentLabel}`,
                                                                ),
                                                        )}
                                                    </div>

                                                    <p className="mt-4 text-white text-md">
                                                        {footerLabel}
                                                    </p>

                                                    {bucket.alias === "next7days" &&
                                                        renderDueTodayButton(
                                                            bucket.dueTodayRows
                                                                .length,
                                                            () =>
                                                                handleDueDateBucketSelection(
                                                                    bucket,
                                                                    bucket.dueTodayRows,
                                                                    `Due today ${currentAssignmentLabel}`,
                                                                ),
                                                        )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>

                            <div ref={tableSectionRef} className="mt-10">
                                <h2
                                    className={`text-2xl font-bold ${
                                        isDueTodayLabel(selectedTableView?.title)
                                            ? getDueTodayTextClass()
                                            : "text-black"
                                    }`}
                                >
                                    {selectedTableView?.title ||
                                        currentAssignmentLabel}
                                </h2>
                                <p className="mt-2 text-sm text-slate-500">
                                    Showing {tableData.length}{" "}
                                    {currentAssignmentLabelLower} for{" "}
                                    {appliedEmployeeLabel}.
                                </p>

                                {selectedTableView && (
                                    <button
                                        type="button"
                                        onClick={() => setSelectedTableView(null)}
                                        className="inline-flex items-center mt-3 text-sm font-medium text-teal-600 transition hover:text-teal-700"
                                    >
                                        Clear filter
                                    </button>
                                )}

                                <div className="mt-6">
                                    {appliedFilters.assignmentType ===
                                    ASSIGNMENT_TYPES.JOBS ? (
                                        <JobAccountabilityTable
                                            data={tableData}
                                            onHandleClickNotes={onHandleClickNotes}
                                            canWriteSteps={canWriteSteps}
                                            canNavigateToService={
                                                canReadServices
                                            }
                                            canReadSteps={canReadSteps}
                                        />
                                    ) : (
                                        <ServicesAccountabilityTable
                                            data={tableData}
                                            onHandleClickNotes={onHandleClickNotes}
                                            canReadServices={
                                                canReadServices
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
}

export default Accountability;
